import { getConsentStatus } from "./consent";
import { whenMarketingMayLoad } from "./marketing-load";

// Microsoft Advertising (Bing) Universal Event Tracking tag.
// Tag ID 343277527, provided by the account owner.
//
// Page views (including client-side route changes) are tracked automatically
// by the tag via enableAutoSpaTracking, so trackPageview does not push them.
// Conversion events (purchase, quote lead, sign-up) are pushed from the
// shared analytics module.
//
// Runs on the live site only; on the dev preview add ?uet_debug=1 to the URL
// to enable it. Honors the cookie-banner opt-out like the other ad pixels.

const TAG_ID = "343277527";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uetq?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    UET?: any;
  }
}

function debugMode(): boolean {
  try {
    if (new URLSearchParams(window.location.search).has("uet_debug")) {
      sessionStorage.setItem("uet_debug", "1");
    }
    return sessionStorage.getItem("uet_debug") === "1";
  } catch {
    return false;
  }
}

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.PROD && !debugMode()) return false;
  try {
    return getConsentStatus() !== "opted-out";
  } catch {
    return true;
  }
}

let initialized = false;

/**
 * Installs the uetq queue now and loads the UET tag (Microsoft's standard
 * snippet) on the shared marketing-load trigger. No-op off the live site or
 * after opt-out.
 */
export function initMicrosoftUet(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;
  try {
    const w = window as Window & Record<string, any>;
    w.uetq = w.uetq || [];
    const o: Record<string, unknown> = { ti: TAG_ID, enableAutoSpaTracking: true, ts: Date.now() };
    whenMarketingMayLoad(() => {
      // Visitor may have opted out between init and the trigger.
      if (!isEnabled()) return;
      const n = document.createElement("script") as HTMLScriptElement & { onreadystatechange?: unknown; readyState?: string };
      n.src = `https://bat.bing.net/bat.js?ti=${TAG_ID}`;
      n.async = true;
      n.onload = (n as any).onreadystatechange = function (this: any) {
        const s = this.readyState;
        if (s && s !== "loaded" && s !== "complete") return;
        if (!w.UET) return;
        o.q = w.uetq;
        w.uetq = new w.UET(o);
        w.uetq.push("pageLoad");
        n.onload = (n as any).onreadystatechange = null;
      };
      const first = document.getElementsByTagName("script")[0];
      if (first?.parentNode) first.parentNode.insertBefore(n, first);
      else document.head.appendChild(n);
    });
  } catch {
    // Analytics must never break the app.
  }
}

// ─── Enhanced conversions ────────────────────────────────────────────────────
// Microsoft matches conversions to signed-in Microsoft users using the
// visitor's email/phone. We normalise them exactly as Microsoft specifies and
// send only SHA-256 hashes — the raw values never leave the browser.

/** Microsoft's email rules: no whitespace/accents, drop "+tag" and dots before "@", lower case. */
export function normalizeUetEmail(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let e = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "").toLowerCase();
  const at = e.lastIndexOf("@");
  if (at <= 0) return null;
  let local = e.slice(0, at);
  let domain = e.slice(at + 1);
  const plus = local.indexOf("+");
  if (plus >= 0) local = local.slice(0, plus);
  local = local.replace(/\./g, "");
  domain = domain.replace(/\.+$/, "");
  if (!local || !domain.includes(".")) return null;
  e = `${local}@${domain}`;
  return e;
}

/** E.164 (+15551234567). Bare 10-digit numbers are treated as US; anything unclear is dropped. */
export function normalizeUetPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

async function sha256Hex(value: string): Promise<string | null> {
  try {
    const subtle = window.crypto?.subtle;
    if (!subtle) return null;
    const buf = await subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

// Events wait for any in-flight hashing so the user data is set before the
// conversion that should carry it.
let userDataReady: Promise<void> = Promise.resolve();
let lastUserDataKey = "";

/** Sets hashed email/phone for enhanced conversions. Safe to call repeatedly. */
export function uetSetUserData(user: { email?: string | null; phone?: string | null }): void {
  if (!isEnabled()) return;
  const email = normalizeUetEmail(user.email);
  const phone = normalizeUetPhone(user.phone);
  if (!email && !phone) return;
  const key = `${email ?? ""}|${phone ?? ""}`;
  if (key === lastUserDataKey) return;
  lastUserDataKey = key;
  if (!initialized) initMicrosoftUet();
  const previous = userDataReady;
  userDataReady = (async () => {
    await previous;
    const [em, ph] = await Promise.all([email ? sha256Hex(email) : null, phone ? sha256Hex(phone) : null]);
    if (!em && !ph) return;
    try {
      window.uetq = window.uetq || [];
      window.uetq.push("set", { pid: { ...(em ? { em } : {}), ...(ph ? { ph } : {}) } });
    } catch {
      // Analytics must never break the app.
    }
  })();
}

/** Pushes a UET custom event, e.g. uetEvent("purchase", { revenue_value: 99, currency: "USD" }). */
export function uetEvent(action: string, params: Record<string, unknown> = {}): void {
  if (!isEnabled()) return;
  if (!initialized) initMicrosoftUet();
  void userDataReady.then(() => {
    try {
      window.uetq = window.uetq || [];
      window.uetq.push("event", action, params);
    } catch {
      // Analytics must never break the app.
    }
  });
}
